import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeleteInvoicesMutation,
    useInvoicesInfiniteQuery,
    useSendInvoicesMutation,
} from './useInvoicesQuery';
import {useInvoicesSearch} from './useInvoicesSearch';
import {useInvoicesTableColumns} from './useInvoicesTableColumns';
import {InvoicesFilters} from './useInvoicesFilter';
import {Invoice} from '../types';

/**
 * Default sort: NEWEST FIRST, on `createdAtMillis`.
 *
 * The key must be immutable and non-null. `created` is set by Stripe at
 * creation and never moves, so it satisfies both — unlike `updated`, which
 * would reorder the list under an operator who is reading it. It is also the
 * order the API returns (Stripe lists invoices newest-first), so the first
 * paint AGREES with the server rather than reshuffling it.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Invoice> = {
    columnAccessor: 'createdAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against. `id` and `number` are both included
 * because an operator chasing a specific invoice pastes either — the Stripe id
 * (`in_...`) from the dashboard, or the human invoice number from a customer
 * email.
 */
const matchesKeyword = (item: Invoice, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.number,
        item.customerId,
        item.customerName,
        item.customerEmail,
        item.description,
        item.status,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active filter. Filters are exact matches on
 * a stringified field; an empty value means the field is unset and is ignored,
 * so a cleared drawer field does not exclude everything.
 */
const matchesFilters = (item: Invoice, filters: InvoicesFilters): boolean =>
    Object.entries(filters)
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof Invoice];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF SORT AND FILTER: the pages FETCHED SO FAR.
 *
 * `listInvoices` accepts an `InvoicesFilterInput`, and this module uses it for
 * exactly one narrowing — `customerId`, which the customer detail page's
 * Invoices tab passes — so THAT filter is genuinely applied by the server. The
 * drawer's status/paid choices are not fields that input declares, and there is
 * no sort argument at all, so both are applied in the browser over the loaded
 * window. Sorting page 1 does not sort the dataset, and a keyword matches only
 * rows already received.
 *
 * This is a property of the token-pagination model against this API, not a bug
 * and not an oversight. It is also exactly why the footer states a LOADED count
 * and never a total: see DataTableTokenPagination, which is passed `filterTerm`
 * so a filtered view says "N of M loaded records match" rather than implying it
 * searched everything.
 *
 * Widening the server-side filter is a backend change to InvoicesFilterInput,
 * not a footer fix.
 */
export const useInvoices = (
    customerId?: string,
    filters: InvoicesFilters = {},
) => {
    const query = useInvoicesInfiniteQuery(customerId);
    const {columns} = useInvoicesTableColumns();
    const {keyword} = useInvoicesSearch();
    const deleteMutation = useDeleteInvoicesMutation();
    const sendMutation = useSendInvoicesMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: Invoice[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // Normalise Stripe's epoch-seconds into millis ONCE, here, so the
        // Created column can both render and sort the same value.
        .map(item => ({
            ...item,
            createdAtMillis: Number.isFinite(Number(item.created))
                ? Number(item.created) * 1000
                : undefined,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare. Do not
    // hand-roll one; the null rule is what a hand-rolled version gets wrong.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<Invoice>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched`
    // is rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        sendInvoices: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                sendMutation.mutate(ids);
            }
        },
        deleteMutation,
        sendMutation,
    };
};
